import { useQuery } from '@tanstack/react-query'
import { analyticsService } from '@/services/api'
import {
  Users, UserCheck, UserX, Clock, Activity, CheckCircle2,
  Building2, ShieldCheck, Monitor, MapPin,
  TrendingUp, BarChart3, PieChart as PieIcon, ChevronRight,
  GraduationCap, UserCog, AlertTriangle, X
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import StatPersonList from '@/components/dashboard/StatPersonList'

// ── Detect mobile ────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return isMobile
}

// ── Stat Detail Modal (desktop) ──────────────────────────
const STAT_TITLES = {
  inside_now:   'Сейчас внутри',
  absent_today: 'Не явились',
  late_total:   'Опоздали',
  on_time:      'Вовремя',
  students_in:  'Студентов сегодня',
  employees_in: 'Сотрудников сегодня',
  total_in:     'Всего вошли',
  total_out:    'Всего вышли',
  no_schedule:  'Без расписания',
}

function StatModal({ stat, onClose }) {
  const title = STAT_TITLES[stat] || 'Список'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-[24px] shadow-2xl border border-slate-200 w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-black text-slate-900 tracking-tight">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X size={18} />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-hidden p-4">
          <StatPersonList stat={stat} title={title} />
        </div>
      </div>
    </div>
  )
}

// ── Tooltip ─────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-2xl p-4">
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 border-b border-slate-50 pb-2">{label}</p>
        <div className="space-y-2">
          {payload.map((entry, i) => (
            <div key={i} className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-[11px] font-bold text-slate-600">{entry.name}</span>
              </div>
              <span className="text-[11px] font-black text-slate-900">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }
  return null
}

const STATUS_CONFIG = {
  on_time:   { label: 'CLEARED',  bg: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  late:      { label: 'DELAYED',  bg: 'bg-amber-50 text-amber-600 border-amber-100' },
  very_late: { label: 'CRITICAL', bg: 'bg-rose-50 text-rose-600 border-rose-100' },
  absent:    { label: 'MISSING',  bg: 'bg-slate-50 text-slate-500 border-slate-200' },
}

// ── Mini Stat Card ───────────────────────────────────────
function MiniStat({ label, value, sub, icon: Icon, color, onClick }) {
  const themes = {
    indigo:  'text-indigo-600 bg-indigo-50 border-indigo-100',
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    amber:   'text-amber-600 bg-amber-50 border-amber-100',
    rose:    'text-rose-600 bg-rose-50 border-rose-100',
    violet:  'text-violet-600 bg-violet-50 border-violet-100',
    blue:    'text-blue-600 bg-blue-50 border-blue-100',
  }
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag onClick={onClick} className={`bg-white border border-slate-200 rounded-[20px] p-5 md:p-6 flex items-center gap-4 shadow-sm w-full text-left ${onClick ? 'hover:border-slate-300 hover:shadow-md transition-all cursor-pointer' : ''}`}>
      <div className={`p-3 rounded-2xl border shrink-0 ${themes[color]}`}>
        <Icon size={20} strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 truncate">{label}</p>
        <p className="text-2xl font-black text-slate-900 tracking-tighter leading-none mt-1">{value ?? 0}</p>
        {sub && <p className="text-[10px] font-bold text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </Tag>
  )
}

// ── Section Header ───────────────────────────────────────
function SectionHeader({ icon: Icon, title, sub, right }) {
  return (
    <div className="flex items-center justify-between mb-6 md:mb-8">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-slate-50 rounded-xl border border-slate-100">
          <Icon size={16} className="text-slate-600" />
        </div>
        <div>
          <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">{title}</h3>
          {sub && <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">{sub}</p>}
        </div>
      </div>
      {right}
    </div>
  )
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [activeStat, setActiveStat] = useState(null)

  const openStat = useCallback((stat) => {
    if (isMobile) {
      navigate(`/dashboard/stat/${stat}`)
    } else {
      setActiveStat(stat)
    }
  }, [isMobile, navigate])

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analyticsService.dashboard().then((r) => r.data),
    refetchInterval: 30000,
    keepPreviousData: true,
  })

  const { data: trend } = useQuery({
    queryKey: ['trend', 7],
    queryFn: () => analyticsService.trend({ days: 7 }).then((r) => r.data),
    refetchInterval: 60000,
  })

  const { data: activityFlow } = useQuery({
    queryKey: ['activity-flow'],
    queryFn: () => analyticsService.activityFlow().then((r) => r.data),
    refetchInterval: 60000,
  })

  const [liveEvents, setLiveEvents] = useState([])
  useEffect(() => {
    const host = window.location.host
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(`${protocol}://${host}/ws/attendance/`)
    ws.onmessage = (e) => {
      const event = JSON.parse(e.data)
      setLiveEvents((prev) => [event, ...prev].slice(0, 10))
    }
    return () => ws.close()
  }, [])

  const recentEvents = useMemo(() => {
    const fetched = dashboard?.recent_events || []
    const fetchedIds = new Set(fetched.map((l) => l.id))
    const uniqueLive = liveEvents.filter((l) => !fetchedIds.has(l.id))
    return [...uniqueLive, ...fetched].slice(0, 12)
  }, [dashboard, liveEvents])

  if (isLoading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="h-10 w-10 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  )

  const d = dashboard || {}
  // % among people WITH schedules only (excludes no_schedule)
  const scheduledTotal = (d.on_time || 0) + (d.late || 0) + (d.very_late || 0)
  const onTimePct = scheduledTotal > 0 ? Math.round((d.on_time / scheduledTotal) * 100) : 0

  return (
    <>
    {activeStat && <StatModal stat={activeStat} onClose={() => setActiveStat(null)} />}
    <div className="min-h-screen bg-[#F8F9FB] p-4 md:p-6 lg:p-12 text-slate-900 font-sans selection:bg-indigo-100">

      {/* HEADER */}
      <header className="max-w-7xl mx-auto mb-6 md:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-100">
              <ShieldCheck className="text-white w-5 h-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-600">Control Dashboard</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter leading-none mb-2">Обзор системы.</h1>
          <p className="text-slate-400 font-medium text-sm hidden sm:block">Мониторинг посещаемости и аналитика в реальном времени.</p>
        </div>
        <div className="flex items-center gap-3 px-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm w-fit">
          <div className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </div>
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-700">Live</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">

        {/* ── ATTENDANCE BOX ──────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] shadow-sm overflow-hidden">
          <div className="p-5 md:p-8 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/30">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="p-2.5 md:p-3 bg-indigo-600 rounded-xl md:rounded-2xl text-white shadow-lg shadow-indigo-200">
                <Activity size={20} />
              </div>
              <div>
                <h3 className="text-base md:text-xl font-black text-slate-900 tracking-tight">Посещаемость за сегодня</h3>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-0.5 hidden sm:block">Daily Attendance Overview</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm font-bold text-slate-600">
              <button onClick={() => openStat('total_in')} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs">Входов:</span>
                <span className="text-emerald-600 font-black">{d.total_in || 0}</span>
              </button>
              <div className="w-px h-5 bg-slate-200" />
              <button onClick={() => openStat('total_out')} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-xs">Выходов:</span>
                <span className="text-blue-600 font-black">{d.total_out || 0}</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {[
              { label: 'СЕЙЧАС ВНУТРИ', value: d.inside_now || 0, icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-50 border-indigo-100', stat: 'inside_now' },
              { label: 'НЕ ЯВИЛИСЬ',   value: d.absent_today || 0, icon: UserX, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-100', stat: 'absent_today' },
              { label: 'ОПОЗДАЛИ',     value: d.late_total || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100', stat: 'late_total' },
              { label: 'ВОВРЕМЯ',      value: `${onTimePct}%`, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100', stat: 'on_time' },
            ].map(({ label, value, icon: Icon, color, bg, stat }) => (
              <button key={label} onClick={() => openStat(stat)} className="p-5 md:p-8 flex items-center gap-4 hover:bg-slate-50/80 transition-colors cursor-pointer text-left w-full">
                <div className={`w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl border flex items-center justify-center shrink-0 ${bg}`}>
                  <Icon strokeWidth={2.5} size={22} className={color} />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</p>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className={`text-2xl md:text-4xl font-black tracking-tighter ${color}`}>{value}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── MINI STATS: Students / Employees / Avg Late ──────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MiniStat label="Студентов сегодня" value={d.students_in || 0}  icon={GraduationCap} color="blue"    sub="вошли"   onClick={() => openStat('students_in')} />
          <MiniStat label="Сотрудников"        value={d.employees_in || 0} icon={UserCog}       color="violet"  sub="вошли"   onClick={() => openStat('employees_in')} />
          <MiniStat label="Среднее опоздание"  value={`${d.avg_late_minutes || 0} мин`} icon={Clock} color="amber" />
          <MiniStat label="Вовремя"            value={d.on_time || 0} icon={UserCheck} color="emerald" sub="человек" onClick={() => openStat('on_time')} />
        </div>

        {/* ── TREND + INTEGRITY ────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">

          {/* 7-Day Area Chart */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] p-5 md:p-8 shadow-sm">
            <SectionHeader icon={TrendingUp} title="Attendance Flow" sub="Last 7 Days" />
            <div className="h-[200px] md:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend?.data || []}>
                  <defs>
                    <linearGradient id="softIndigo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#4f46e5" stopOpacity={0.08} />
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="softEmerald" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#10b981" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                    tickFormatter={(v) => format(new Date(v), 'EEE', { locale: ru }).toUpperCase()} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} dx={-10} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total"   name="Всего"   stroke="#4f46e5" strokeWidth={3} fill="url(#softIndigo)"  activeDot={{ r: 6, strokeWidth: 0, fill: '#4f46e5' }} />
                  <Area type="monotone" dataKey="on_time" name="Вовремя" stroke="#10b981" strokeWidth={2} fill="url(#softEmerald)" activeDot={{ r: 4, strokeWidth: 0, fill: '#10b981' }} />
                  <Area type="monotone" dataKey="late"    name="Опоздали" stroke="#f59e0b" strokeWidth={2} fill="transparent" activeDot={{ r: 4, strokeWidth: 0, fill: '#f59e0b' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Integrity Donut */}
          <div className="bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] p-5 md:p-8 shadow-sm flex flex-col">
            <SectionHeader icon={PieIcon} title="Integrity Rating" />
            <div className="relative flex-1 min-h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.status_distribution || []} innerRadius={70} outerRadius={95} paddingAngle={8} dataKey="value" stroke="none">
                    {(d.status_distribution || []).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-4xl font-black text-slate-900 tracking-tighter">{onTimePct}%</span>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Cleared</span>
              </div>
            </div>
            {/* Legend */}
            <div className="space-y-2 mt-4">
              {(d.status_distribution || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="text-[11px] font-bold text-slate-600">{s.name}</span>
                  </div>
                  <span className="text-[11px] font-black text-slate-900">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── ACTIVITY FLOW + STUDENTS VS EMPLOYEES ────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">

          {/* Hourly Activity Bar Chart */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] p-5 md:p-8 shadow-sm">
            <SectionHeader icon={BarChart3} title="Активность по часам" sub="Today's arrival distribution" />
            <div className="h-[200px] md:h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityFlow || []} barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="hour" axisLine={false} tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 9, fontWeight: 700 }}
                    interval={1} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} dx={-10} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Посещений" radius={[6, 6, 0, 0]}>
                    {(activityFlow || []).map((entry, i) => (
                      <Cell key={i} fill={entry.count > 0 ? '#4f46e5' : '#e2e8f0'} fillOpacity={entry.count > 0 ? 0.85 : 1} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Students vs Employees */}
          <div className="bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] p-5 md:p-8 shadow-sm flex flex-col">
            <SectionHeader icon={Users} title="Состав сегодня" sub="Students vs Staff" />
            <div className="relative flex-1 min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.type_distribution || []} innerRadius={65} outerRadius={90} paddingAngle={6} dataKey="value" stroke="none">
                    {(d.type_distribution || []).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-3xl font-black text-slate-900 tracking-tighter">{d.total_in || 0}</span>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Всего</span>
              </div>
            </div>
            <div className="space-y-3 mt-4">
              {(d.type_distribution || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="text-[12px] font-bold text-slate-600">{t.name}</span>
                  </div>
                  <span className="text-[12px] font-black text-slate-900">{t.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── DEPT PERFORMANCE + TOP GROUPS ────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">

          {/* Department Performance */}
          <div className="bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] p-5 md:p-8 shadow-sm">
            <SectionHeader icon={Building2} title="Посещаемость по кафедрам" sub="Today, % от состава" />
            <div className="space-y-3">
              {(d.department_performance || []).slice(0, 8).map((dept, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-[10px] font-black text-slate-400 w-4 shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-bold text-slate-700 truncate pr-2">{dept.name}</span>
                      <span className="text-[11px] font-black text-slate-900 shrink-0">{dept.value}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${dept.value}%`,
                          backgroundColor: dept.value >= 80 ? '#10b981' : dept.value >= 60 ? '#f59e0b' : '#ef4444'
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 w-8 text-right shrink-0">{dept.total}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Groups + Worst Departments */}
          <div className="space-y-4 md:space-y-6">

            {/* Top Groups */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-5 md:p-6 shadow-sm">
              <SectionHeader icon={GraduationCap} title="Топ групп" sub="This month, attendance %" />
              <div className="space-y-2.5">
                {(d.top_groups || []).slice(0, 5).map((g, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black shrink-0 ${
                      i === 0 ? 'bg-amber-100 text-amber-600' :
                      i === 1 ? 'bg-slate-100 text-slate-600' :
                      i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-slate-50 text-slate-400'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-slate-800 truncate">{g.group}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{g.department}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-[11px] font-black ${g.on_time_percent >= 80 ? 'text-emerald-600' : g.on_time_percent >= 60 ? 'text-amber-600' : 'text-rose-600'}`}>
                        {g.on_time_percent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Worst Departments (most late) */}
            <div className="bg-white border border-slate-200 rounded-[24px] p-5 md:p-6 shadow-sm">
              <SectionHeader icon={AlertTriangle} title="Опоздания по отделам" sub="Today, количество" />
              <div className="space-y-2.5">
                {(d.worst_departments || []).filter(x => x.late_count > 0).slice(0, 5).map((dept, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-bold text-slate-700 truncate">{dept.department}</span>
                    <span className="px-2.5 py-1 rounded-lg bg-rose-50 text-rose-600 border border-rose-100 text-[10px] font-black shrink-0">
                      {dept.late_count} опозд.
                    </span>
                  </div>
                ))}
                {!(d.worst_departments || []).some(x => x.late_count > 0) && (
                  <p className="text-[11px] text-slate-400 font-bold text-center py-2">Опозданий сегодня нет</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── EVENT LOG ────────────────────────────────────────── */}
        <section className="bg-white border border-slate-200 rounded-[24px] md:rounded-[32px] overflow-hidden shadow-sm">
          <div className="p-5 md:p-8 border-b border-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="p-2.5 md:p-3 bg-slate-900 rounded-xl md:rounded-2xl text-white"><Monitor size={20} /></div>
              <div>
                <h3 className="text-base md:text-xl font-black text-slate-900 tracking-tight">Журнал событий</h3>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-0.5 hidden sm:block">Real-time Data Protocol</p>
              </div>
            </div>
            <button className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600 transition-colors">
              Все <ChevronRight size={14} />
            </button>
          </div>

          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 border-b border-slate-100">
                  <th className="px-8 py-5">Время</th>
                  <th className="px-8 py-5">Идентификатор</th>
                  <th className="px-8 py-5 text-center">Действие</th>
                  <th className="px-8 py-5 text-center">Протокол</th>
                  <th className="px-8 py-5 text-right">Устройство</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentEvents.map((log) => {
                  const status = STATUS_CONFIG[log.attendance_status] || STATUS_CONFIG.absent
                  return (
                    <tr key={log.id} className="hover:bg-slate-50/30 transition-colors">
                      <td className="px-8 py-5">
                        <span className="font-mono text-xs font-bold text-indigo-600">
                          {format(new Date(log.timestamp), 'HH:mm:ss')}
                        </span>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-400 border border-slate-200 shrink-0">
                            {log.person_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <span className="text-sm font-bold text-slate-700">{log.person_name}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border ${log.event_type === 'in' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                          {log.event_display}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black border ${status.bg}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <div className="flex items-center justify-end gap-2 text-slate-400 font-bold text-[10px]">
                          <MapPin size={12} />
                          {log.device_name || 'NODE_01'}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="md:hidden divide-y divide-slate-50">
            {recentEvents.map((log) => {
              const status = STATUS_CONFIG[log.attendance_status] || STATUS_CONFIG.absent
              return (
                <div key={log.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-400 border border-slate-200 shrink-0">
                    {log.person_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-700 truncate">{log.person_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-[11px] font-bold text-indigo-600">{format(new Date(log.timestamp), 'HH:mm')}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-[11px] text-slate-400">{log.device_name || 'NODE_01'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${log.event_type === 'in' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                      {log.event_display}
                    </span>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${status.bg}`}>
                      {status.label}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </div>
    </>
  )
}
